import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe } from 'modules/PermissionsAdmin/api/permissionsApi';
import { hasEntitlement as permissionsHaveEntitlement } from './entitlements';

/**
 * Post-Mongo-pivot, role/permission data no longer lives in the Clerk JWT.
 * `useAuth().has({ permission, role })` reads stale or empty data for
 * migrated users, so every authorization check in the app routes through
 * this context instead.
 *
 * The provider fetches /permissions/me once per session+org via react-query
 * and exposes:
 *   - `me`     — the raw payload (or null while loading / signed out)
 *   - `orgId`  — the org the current `me`/`status` describes. Echoed back so a
 *        caller can tell WHICH org has settled — OrgBoundary's switch gate needs
 *        that to distinguish "the old org's payload is still ready" from "the new
 *        org's payload has landed".
 *   - `has({ permission, role })` — the drop-in `useAuth().has` analogue
 *        • isAdmin bypasses permission checks (capability) but NOT role checks
 *          (identity), so `has({ role: 'org:distributor' })` still requires
 *          actual membership.
 *   - `status` — loading | error | ready. Gates only deny in `ready`.
 *   - `refresh()` — manually invalidate the cached payload (rarely needed;
 *                   mutations in usePermissionsAdmin already invalidate)
 *
 * `has()` accepts both bare (`diagrams:read`) and prefixed (`org:diagrams:read`)
 * forms so the existing call sites that pass `org:{resource}:{action}` keep
 * working unchanged.
 */

export const PERMISSIONS_ME_QUERY_KEY = ['permissions', 'me'];

// /permissions/me computes its snapshot from a live Clerk membership lookup.
// For an account provisioned moments earlier (a magic-link signup creates the
// user, org, and membership right before the app's first load) that lookup can
// lag and come back empty — a 200 deny-all payload indistinguishable from a
// genuine "no access". Treat such snapshots as still-loading for a bounded
// grace window (polling meanwhile) so the route gates don't flash a denial at
// a user whose admin role simply isn't readable yet. A real member always has
// at least their role slug in `roles`, so this only matches the lag shape.
export const PROVISIONING_GRACE_MS = 30_000;
export const PROVISIONING_POLL_MS = 2_000;

export const isProvisionalDenyAll = (me) =>
  Boolean(me) &&
  !me.isAdmin &&
  (!Array.isArray(me.roles) || me.roles.length === 0) &&
  (!Array.isArray(me.permissions) || me.permissions.length === 0);

const stripOrg = (s) => (typeof s === 'string' && s.startsWith('org:') ? s.slice(4) : s);

const PermissionsContext = createContext({
  me: null,
  orgId: null,
  status: 'loading',
  has: () => false,
  hasEntitlement: () => false,
  refresh: async () => {},
});

/**
 * `orgId` — the organization to fetch permissions for. OrgBoundary passes
 * Clerk's AUTHORITATIVE org id (the one from `clerk.addListener`), because
 * `useAuth().orgId` lags a same-session `setActive()` by a beat: keying the
 * query on the hook meant the key churned AFTER a switch had already settled
 * (old key → new key → `data` undefined → back to `loading`), replaying the
 * whole deny-while-loading window with the app fully revealed and no loader
 * anywhere. It also cached the new org's payload under the old org's key.
 *
 * Omit the prop to fall back to the hook (standalone unit tests, any
 * out-of-boundary usage). `null` is a real value meaning "no org yet" — it
 * disables the query — so the fallback keys on `undefined`, not falsiness.
 *
 * This file lives in Common/, which must not import from feature modules, so
 * the authoritative org arrives as a prop rather than via App's ActiveOrgContext.
 */
export const PermissionsProvider = ({
  orgId: orgIdProp,
  children,
  provisioningGraceMs = PROVISIONING_GRACE_MS,
  provisioningPollMs = PROVISIONING_POLL_MS,
}) => {
  const { orgId: hookOrgId, isSignedIn, isLoaded } = useAuth();
  const orgId = orgIdProp === undefined ? (hookOrgId ?? null) : orgIdProp;
  const qc = useQueryClient();
  const enabled = Boolean(isLoaded && isSignedIn && orgId);

  // The grace clock starts when the query can actually run — starting at
  // mount would burn the window while Clerk is still activating the org
  // client-side (the magic-link boot sequence does exactly that).
  const [graceExpired, setGraceExpired] = useState(false);
  // Re-arm the window whenever the org changes: the provider now stays
  // mounted across org switches (OrgBoundary mounts it ABOVE its remount
  // key), so a one-shot clock would leave any org entered after the first
  // 30s of uptime with no grace at all. Safe against a same-frame provisional
  // payload: an org change also changes the query key, so no data exists for
  // the new org until after this reset has run.
  useEffect(() => {
    setGraceExpired(false);
  }, [orgId]);
  useEffect(() => {
    if (!enabled || graceExpired) return undefined;
    const timer = setTimeout(() => setGraceExpired(true), provisioningGraceMs);
    return () => clearTimeout(timer);
  }, [enabled, graceExpired, provisioningGraceMs]);

  const { data, isError, isSuccess } = useQuery({
    queryKey: [...PERMISSIONS_ME_QUERY_KEY, orgId || null],
    queryFn: getMe,
    enabled,
    // Mirror Clerk's JWT TTL — frequent enough that role changes propagate
    // quickly, infrequent enough that we don't thrash on every render.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    // While the snapshot still looks provisional and the grace window is
    // open, poll for the real one; anything else stops the polling.
    refetchInterval: (query) =>
      !graceExpired && isProvisionalDenyAll(query.state.data) ? provisioningPollMs : false,
    // A background refetch (focus, staleTime) can hit the same Clerk read lag
    // mid-session and come back deny-all. Adopting it would replace an
    // authoritative snapshot and bounce the user off the page they're on —
    // the successful-refetch twin of the "data-first status" rule above, which
    // only covers refetch *errors*. Keep the data we trust; the next scheduled
    // refetch picks up the settled truth. A genuine mid-session revocation is
    // not this shape: removing a member kills the membership (the request
    // 401/403s), and a real member always retains at least a role slug.
    structuralSharing: (oldData, newData) =>
      isProvisionalDenyAll(newData) && oldData && !isProvisionalDenyAll(oldData)
        ? oldData
        : newData,
  });

  // TanStack Query keeps cached data after a background refetch error; serve it.
  const provisioning = !graceExpired && isProvisionalDenyAll(data);
  const status =
    !enabled || provisioning
      ? 'loading'
      : data
        ? 'ready'
        : isError || isSuccess
          ? 'error'
          : 'loading';

  const has = useCallback(
    ({ permission, role } = {}) => {
      if (!data) return false;
      if (permission) {
        if (data.isAdmin) return true;
        const slug = stripOrg(permission);
        return Array.isArray(data.permissions) && data.permissions.includes(slug);
      }
      if (role) {
        const slug = stripOrg(role);
        return Array.isArray(data.roles) && data.roles.includes(slug);
      }
      return false;
    },
    [data],
  );

  const hasEntitlement = useCallback(
    (entitlement) => permissionsHaveEntitlement(data, entitlement),
    [data],
  );

  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: PERMISSIONS_ME_QUERY_KEY }),
    [qc],
  );

  const value = useMemo(
    () => ({
      me: data || null,
      orgId,
      status,
      has,
      hasEntitlement,
      refresh,
    }),
    [data, orgId, status, has, hasEntitlement, refresh],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissionsContext = () => useContext(PermissionsContext);
